import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import PrimaryButton from '../../common/buttons/PrimaryButton';
import OtpInput from '../../common/fields/OtpInput';
import useAuth from '../../../hooks/useAuth';
import { resendOtpRequest } from '../../../api/auth.api';

const RESEND_COOLDOWN = 60;

export default function VerifyEmail({ email, onClose }) {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await verifyEmail(email, otp);
      onClose();

      if (res.user) {
        navigate(`/teacher/${res.user.slug}`, {
          state: { showProfileCompletion: true, isNewUser: true },
        });
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid or expired code.';
      setError(msg);
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      await resendOtpRequest({ email, type: 'email_verification' });
      setCountdown(RESEND_COOLDOWN);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center">
          <MarkEmailRead sx={{ fontSize: 36, color: '#F6911D' }} />
        </div>
      </div>

      <h2 className="text-3xl font-bold text-white mb-2">Check your email</h2>
      <p className="text-gray-400 mb-1">We sent a 6-digit code to</p>
      <p className="text-orange-400 font-semibold mb-8">{email}</p>

      <OtpInput value={otp} onChange={setOtp} disabled={loading} />

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="mt-6">
        <PrimaryButton fullWidth onClick={handleVerify} disabled={loading || otp.length !== 6}>
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Email'}
        </PrimaryButton>
      </div>

      <div className="mt-5 text-sm text-gray-400">
        Didn't receive it?{' '}
        {countdown > 0 ? (
          <span className="text-gray-500">Resend in {countdown}s</span>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-orange-400 hover:text-orange-300 font-medium transition-colors disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend code'}
          </button>
        )}
      </div>
    </div>
  );
}
